'use client';

import type { Collaborator } from '@/app/models/collaborator';
import type { Project } from '@/app/models/project';
import type { NewSpecificAllocationBody } from '@/app/models/specificAllocation';
import { useSearchPersonQuery } from '@/app/services/personApi';
import { useSearchProjectsQuery } from '@/app/services/projectApi';
import {
  useCreateSpecificAllocationMutation,
  useFindAllSpecificAllocationsByProjectQuery,
} from '@/app/services/specificAllocationApi';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { Box, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DrawerButtons from '../common/DrawerButtons';
import TextButton from '../common/TextButton';
import NewSpecificAllocationTable from './NewSpecificAllocationTable';
import PercentInput from '../common/PercentInput';

interface NewSpecificAllocationProps {
  setDrawerOpen: (open: boolean) => void;
  selectedAllocation: string | null;
  setSelectedAllocation: (i: string | null) => void;
}

export default function NewSpecificAllocation(props: NewSpecificAllocationProps) {
  const { t } = useTranslation(['specificAllocation', 'common']);
  const theme = useTheme();
  const { setDrawerOpen, selectedAllocation, setSelectedAllocation } = props;

  const [selectedCollaborators, setSelectedCollaborators] = useState<
    {
      collaborator: Collaborator;
      allocation: number;
      isFinal: boolean;
    }[]
  >([]);

  const [editSelectedCollaborators, setEditSelectedCollaborators] = useState<
    {
      collaborator: Collaborator;
      allocation: number;
      isFinal: boolean;
    }[]
  >([]);

  const [allocationPercent, setAllocationPercent] = useState('0');
  const [final, setFinal] = useState(false);

  const [project, setProject] = useState<Project | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const { data: projects, isFetching: isLoadingProject } = useSearchProjectsQuery({
    searchTerm: projectSearch,
    bringClosed: false,
    page: 0,
    size: 10,
    sortKey: 'name',
    sortDirection: 'ASC',
  });

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaboratorsAutoComplete } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const { data: foundSpecificAllocation } = useFindAllSpecificAllocationsByProjectQuery(selectedAllocation || '', {
    skip: !selectedAllocation,
  });

  const handleChangeInputPercent = (value: string) => {
    const changedValue = value.replace(',', '.');
    if (/^\d*([.,]\d{0,2})?$/.test(changedValue)) {
      setAllocationPercent(changedValue);
    }
  };

  useEffect(() => {
    if (foundSpecificAllocation && foundSpecificAllocation.length > 0) {
      setSelectedCollaborators(
        foundSpecificAllocation.map((item) => {
          return {
            collaborator: item.person as Collaborator,
            allocation: item.allocationPercent,
            isFinal: item.final,
          };
        }),
      );
    }
  }, [foundSpecificAllocation]);

  const handleAddCollaborator = () => {
    if (collaborator) {
      setSelectedCollaborators((prevValue) => [
        ...prevValue,
        { collaborator, allocation: Number.parseFloat(allocationPercent.replace(',', '.')), isFinal: final },
      ]); // substitui
      setCollaborator(null);
      setCollaboratorSearch('');
      setFinal(false);
      setAllocationPercent('0');
      setCollaboratorSearch('');
    }
  };

  const handleRemove = (removedCollaborator: number) => {
    setSelectedCollaborators((prevValue) => prevValue.filter((col) => col.collaborator.id !== removedCollaborator));
  };

  const [createSpecificAllocation] = useCreateSpecificAllocationMutation();

  const handleSubmit = async () => {
    if (!selectedCollaborators || selectedCollaborators.length < 1 || (!project && !selectedAllocation)) return;
    toast.info(t('new.value.sent'));

    // Creates an array with the updated allocation based on the edits
    const mergedCollaborators = selectedCollaborators.map((item) => {
      const edited = editSelectedCollaborators.find((e) => e.collaborator.badge === item.collaborator.badge);
      return {
        ...item,
        allocation: edited ? edited.allocation : item.allocation,
      };
    });

    const requestBody: NewSpecificAllocationBody = {
      projectCode: selectedAllocation || project?.code || '',
      persons: mergedCollaborators.map((col) => ({
        personId: col.collaborator.id,
        allocationPercent: col.allocation,
        final: col.isFinal,
      })),
    };

    await createSpecificAllocation(requestBody);
    setDrawerOpen(false);
    toast.success(t('new.value.added'));
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const alreadyIncluded = (value: Collaborator) => {
    const filteredCollaborators = selectedCollaborators.filter((item) => item.collaborator.id === value.id);
    return filteredCollaborators.length >= 1;
  };

  const isFormValid = selectedCollaborators.length < 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('specific.allocation')} subtitle={t('new.specific.allocation.subtitle') as string} />

        <Box sx={{ mt: 4 }}>
          <Typography variant="fieldHeader" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {`${t('common:project')}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {selectedAllocation && foundSpecificAllocation ? (
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <TextField
                sx={{ flexGrow: 1 }}
                size="small"
                disabled
                value={`${foundSpecificAllocation[0].project?.code} - ${foundSpecificAllocation[0].project?.name}(${foundSpecificAllocation[0].project?.branchName})`}
              />
            </Stack>
          ) : (
            <GenericAutoComplete
              value={project}
              onChange={(value) => {
                setProject(value);
              }}
              equalityTest={(option, value) => option?.code === value?.code}
              getOptionLabel={(option) =>
                `${option?.code} - ${option?.name}${option?.branchName ? ` (${option?.branchName})` : ''}`
              }
              onSearch={(value) => {
                setProjectSearch(value);
              }}
              options={projectSearch !== '' && projects?.content ? projects?.content : []}
              placeholder={t('search.project') || ''}
              loading={isLoadingProject}
              sx={{ width: '100%' }}
            />
          )}
        </Box>

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 4 }} />

        <Box sx={{ mt: 4 }}>
          <Typography variant="fieldHeader" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            {`${t('common:collaborator')}`}
          </Typography>
          <Typography sx={{ mb: 0.5 }} variant="subtitle2">
            {t('collaborator.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GenericAutoComplete
            value={collaborator}
            onChange={(value) => {
              setCollaborator(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) => `${option?.badge} - ${option?.name}` || ''}
            onSearch={(value) => {
              setCollaboratorSearch(value);
            }}
            options={
              collaboratorSearch !== '' && collaboratorsAutoComplete?.content ? collaboratorsAutoComplete?.content : []
            }
            placeholder={t('search.collaborator') as string}
            sx={{ width: '100%' }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={4} sx={{ mt: 2 }}>
          <Box display="flex" flexDirection="column">
            <Typography variant="fieldHeader" mb={1}>
              {`(%) ${t('allocation')}`}
            </Typography>
            <PercentInput value={allocationPercent} onChange={setAllocationPercent} />
          </Box>

          <Box display="flex" flexDirection="column">
            <Typography variant="fieldHeader" mb={1}>
              {`${t('final.sharing')}?`}
            </Typography>
            <RadioGroup row value={final.toString()} onChange={(e) => setFinal(e.target.value === 'true')}>
              <FormControlLabel value="true" control={<Radio color="secondary" />} label={t('common:yes')} />
              <FormControlLabel value="false" control={<Radio color="secondary" />} label={t('common:no')} />
            </RadioGroup>
          </Box>

          <TextButton
            disabled={(!project && !selectedAllocation) || !collaborator || alreadyIncluded(collaborator)}
            sx={{ ml: 'auto' }}
            text={`+ ${t('common:add')}`}
            onClick={handleAddCollaborator}
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <NewSpecificAllocationTable
            itens={selectedCollaborators}
            onRemove={handleRemove}
            editSelectedCollaborators={editSelectedCollaborators}
            setEditSelectedCollaborators={setEditSelectedCollaborators}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={isFormValid} />
      </Box>
    </Box>
  );
}
